import type { MidiNote, MidiTrack, TrackRole } from "./music";

export type PlaybackMode = "audio" | "midi" | "both";
export type OverdubMonitorSource = "off" | "audio" | "midi" | "both";

export type RecordingTiming = {
  plannedStartAudioTime: number | null;
  observedRecordingStartAudioTime: number | null;
  recordingOffsetMs: number;
  manualCalibrationMs: number;
};

export type AudioClipSource = {
  storageKey: string;
  mimeType: string;
  durationMs: number;
};

export type MidiClipContent = {
  notes: MidiNote[];
};

export type TrackKeyAnalysis = {
  tonicIndex: number;
  mode: "major" | "minor";
  confidence: number;
  analyzedAt: string;
};

export type ProjectKeyMapEvent = {
  startTick: number;
  endTick: number | null;
  analysis: TrackKeyAnalysis;
};

export type ProjectClip = {
  id: string;
  trackId: string;
  name: string;
  startTick: number;
  durationTicks: number;
  audio: AudioClipSource | null;
  midi: MidiClipContent | null;
  recordingTiming: RecordingTiming | null;
};

export type ProjectTrack = {
  id: string;
  name: string;
  role: TrackRole;
  clipIds: string[];
  playbackMode: PlaybackMode;
  overdubMonitorSource: OverdubMonitorSource;
  muted: boolean;
  solo: boolean;
  gain: number;
  keyAnalysis: TrackKeyAnalysis | null;
};

export function defaultRecordingTiming(): RecordingTiming {
  return {
    plannedStartAudioTime: null,
    observedRecordingStartAudioTime: null,
    recordingOffsetMs: 0,
    manualCalibrationMs: 0,
  };
}

export function createTrackWithClip(input: {
  trackId: string;
  clipId: string;
  name: string;
  role: TrackRole;
  notes: MidiNote[];
  ppq: number;
  startTick?: number;
  recordingTiming?: RecordingTiming | null;
}): { track: ProjectTrack; clip: ProjectClip } {
  const startTick = Math.max(0, Math.round(input.startTick ?? 0));
  const notes = input.notes.map((note) => ({ ...note }));
  const noteEndTicks = notes.map((note) => Math.round((note.startBeat + note.durationBeats) * input.ppq));
  const durationTicks = Math.max(input.ppq, ...noteEndTicks, 1);
  const clip: ProjectClip = {
    id: input.clipId,
    trackId: input.trackId,
    name: `${input.name} Clip`,
    startTick,
    durationTicks,
    audio: null,
    midi: { notes },
    recordingTiming: input.recordingTiming === undefined ? defaultRecordingTiming() : input.recordingTiming,
  };
  return {
    track: {
      id: input.trackId,
      name: input.name,
      role: input.role,
      clipIds: [input.clipId],
      playbackMode: "midi",
      overdubMonitorSource: "midi",
      muted: false,
      solo: false,
      gain: 1,
      keyAnalysis: null,
    },
    clip,
  };
}

export function clipsForTrack(track: ProjectTrack, clips: ProjectClip[]): ProjectClip[] {
  const byId = new Map(clips.filter((clip) => clip.trackId === track.id).map((clip) => [clip.id, clip]));
  return track.clipIds.flatMap((clipId) => byId.get(clipId) ?? []);
}

export function trackToMidiTrack(track: ProjectTrack, clips: ProjectClip[], ppq: number): MidiTrack {
  const notes = clipsForTrack(track, clips)
    .flatMap((clip) => (clip.midi?.notes ?? []).map((note) => ({
      ...note,
      startBeat: note.startBeat + clip.startTick / ppq,
    })))
    .sort((left, right) => left.startBeat - right.startBeat || left.midi - right.midi);
  return { name: track.name, role: track.role, notes };
}

function audibleTracks(tracks: ProjectTrack[]): ProjectTrack[] {
  const unmuted = tracks.filter((track) => !track.muted);
  return unmuted.some((track) => track.solo) ? unmuted.filter((track) => track.solo) : unmuted;
}

export function playbackMidiTracks(tracks: ProjectTrack[], clips: ProjectClip[], ppq: number): MidiTrack[] {
  return audibleTracks(tracks)
    .filter((track) => track.playbackMode === "midi" || track.playbackMode === "both")
    .map((track) => trackToMidiTrack(track, clips, ppq))
    .filter((track) => track.notes.length > 0);
}

export function overdubMonitorMidiTracks(tracks: ProjectTrack[], clips: ProjectClip[], ppq: number): MidiTrack[] {
  return audibleTracks(tracks)
    .filter((track) => track.overdubMonitorSource === "midi" || track.overdubMonitorSource === "both")
    .map((track) => trackToMidiTrack(track, clips, ppq))
    .filter((track) => track.notes.length > 0);
}
